import { SetStateAction } from 'react';
import { Agent } from '../interfaces/Agent';
import { ResponseObject } from '../interfaces/App';
import { ChatAgent } from '../interfaces/ChatAgent';
import Knowledge, {
  DigestConfig,
  KnowledgeFileData,
  KnowledgeLanguage,
  WebscrapConfig,
  WebscrapFrequency,
} from '../interfaces/Knowledge';
import User from '../interfaces/User';
import {
  createKnowledge,
  createKnowledgeFile,
  deleteKnowledgeFile,
  updateKnowledge,
} from '../services/knowledgeService';
import { updateUser } from '../services/userService';
import { TranslationKey } from '../types/i18n';

const bytesToMegaBytesLabel = (bytes: number, decimals = 2) => {
  return `${(bytes / (1024 * 1024)).toFixed(decimals)} MB`;
};

/**
 * Converts a string to snake_case.
 * @param input - The string to be converted.
 * @returns The snake_case version of the string.
 */
const toSnakeCase = (input: string): string => {
  return input
    .trim()
    .replace(/[^a-zA-Z0-9.\s]/g, '')
    .replace(/\.(?=.*\.)/g, '')
    .replace(/\s+/g, '_')
    .replace(/^_+/g, '')
    .toLowerCase();
};

// Mirrors the backend's IN_PROGRESS_STALE_AFTER (libs/webscrap/kb_sync.py): a run the
// platform kills mid-flight can never clear its own flag, and the daily sweep that clears
// a stale one runs at most once a day, so the frontend applies the same 1h ceiling instead
// of trusting webscrap_in_progress alone.
const WEBSCRAP_IN_PROGRESS_STALE_AFTER_MS = 60 * 60 * 1000;

// True while a webscrap run is actively rewriting this knowledge base's files, so the
// dashboard can block a racing human upload/delete. A flag with no (or an unparseable)
// start time can't be trusted, so - matching the backend sweep - it's treated as cleared
// rather than as blocking forever.
const isWebscrapInProgress = (
  knowledge?: Pick<
    Knowledge,
    'webscrap_in_progress' | 'webscrap_in_progress_since'
  >,
  now: Date = new Date()
): boolean => {
  if (!knowledge?.webscrap_in_progress) return false;

  const since = knowledge.webscrap_in_progress_since;
  if (!since) return false;

  const startedAt = new Date(since).getTime();
  if (Number.isNaN(startedAt)) return false;

  return now.getTime() - startedAt < WEBSCRAP_IN_PROGRESS_STALE_AFTER_MS;
};

const truncateFilename = (filename: string, maxLength = 40): string => {
  if (filename.length < maxLength) return filename;

  const dotIndex = filename.lastIndexOf('.');
  const hasExtension = dotIndex > 0 && dotIndex < filename.length - 1;

  const name = hasExtension ? filename.slice(0, dotIndex) : filename;
  const ext = hasExtension ? filename.slice(dotIndex) : '';

  const keepLength = maxLength - ext.length - 3;
  const front = Math.ceil(keepLength / 2);
  const back = Math.floor(keepLength / 2);

  return name.slice(0, front) + '...' + name.slice(name.length - back) + ext;
};

const getOrCreateKnowledgeId = async (
  existingKnowledgeId: string | undefined,
  createKnowledgeFn: () => Promise<ResponseObject>
): Promise<string> => {
  if (existingKnowledgeId) {
    return existingKnowledgeId;
  }

  const result = await createKnowledgeFn();
  if (result.status !== 'success') {
    throw new Error('Knowledge creation failed');
  }
  return result.message.id;
};

const createNewKnowledge = async (
  name: string,
  userId: string,
  companyId: string,
  language: KnowledgeLanguage
): Promise<ResponseObject> => {
  const knowledgeData: Knowledge = {
    name,
    user_id: userId,
    company_id: companyId,
    language,
  };

  return await createKnowledge(knowledgeData);
};

// Every createKnowledgeFile call writes to the same knowledge base's Cosmos
// document, so firing the whole batch at once (a) maximizes write
// contention on that one document and (b) splits the client's upload
// bandwidth across every in-flight request, both of which show up as
// failed/stalled uploads as the batch grows. A bounded worker pool keeps
// both effects capped regardless of how many files are staged.
const KNOWLEDGE_FILE_UPLOAD_CONCURRENCY = 4;

// Mirrors the backend's own defaults (libs/webscrap/config.py) so a field
// left untouched in the form still round-trips as the value the scraper
// would have used anyway.
export const WEBSCRAP_DEFAULT_MAX_PAGES = 50;
export const WEBSCRAP_DEFAULT_PAGES_PER_PDF = 1;
export const WEBSCRAP_DEFAULT_MAX_DEPTH = 1;

// The inverse of buildWebscrapPayload's source_page handling: a bare string
// means sitemap-on, the object form carries an explicit depth (used for the
// crawl with the sitemap off, or as the fallback when the site has no
// sitemap). With no depth on source_page, the backend's SourceRule.from_raw
// uses the config's top-level max_depth - pass it as defaultMaxDepth so the
// form seeds what the scraper actually uses. Used to seed the form's
// defaultValues from an existing knowledge's webscrap config.
export const parseWebscrapSourcePage = (
  sourcePage?: WebscrapConfig['source_page'],
  defaultMaxDepth: number = WEBSCRAP_DEFAULT_MAX_DEPTH
): { url: string; useSitemap: boolean; maxDepth: number } => {
  if (typeof sourcePage === 'string') {
    return {
      url: sourcePage,
      useSitemap: true,
      maxDepth: defaultMaxDepth,
    };
  }
  if (sourcePage) {
    return {
      url: sourcePage.url,
      useSitemap: sourcePage.use_sitemap ?? true,
      maxDepth: sourcePage.max_depth ?? defaultMaxDepth,
    };
  }
  return { url: '', useSitemap: true, maxDepth: defaultMaxDepth };
};

// The inverse of buildWebscrapPayload's links handling: each entry becomes a
// { url, depth, splitSelector } row, depth/splitSelector left undefined for
// a bare-string entry (or one with no depth/split_selector) so
// WebscrapLinksField shows empty depth/selector inputs. Used to seed the
// form's defaultValues from an existing knowledge's webscrap config.
export const parseWebscrapLinks = (
  links?: WebscrapConfig['links']
): { url: string; depth?: number; splitSelector?: string }[] => {
  return (links ?? []).map(link =>
    typeof link === 'string'
      ? { url: link }
      : { url: link.url, depth: link.depth, splitSelector: link.split_selector }
  );
};

// Mirrors the backend's own DigestRule defaults (libs/webscrap/config.py)
// so a field left untouched in the form still round-trips as the value the
// scraper would have used anyway.
export const DIGEST_DEFAULT_MAX_ITEMS = 10;
export const DIGEST_DEFAULT_MAX_PAGES = 20;

// The form's per-digest row shape, registered under webscrap_digests by
// DigestsConfigFields - field names match the backend's DigestRule
// (libs/webscrap/config.py) directly, so no renaming is needed between the
// form and the wire payload.
export type DigestFormFields = {
  heading: string;
  url: string;
  item_selector: string;
  title_selector: string;
  date_attr: string;
  drop_past: boolean;
  max_items?: number;
  page_url_template?: string;
  fetch_detail_pages: boolean;
  detail_link_selector?: string;
  max_pages?: number;
  // The digest as loaded, carried with its row (field-array rows move as a
  // whole on add/remove) so buildDigestsPayload can keep the DigestRule
  // fields this form has no inputs for. Absent on a row added in the UI.
  stored?: DigestConfig;
};

// The inverse of buildDigestsPayload: seeds the form's defaultValues from an
// existing knowledge's webscrap.digests.
export const parseDigests = (
  digests?: WebscrapConfig['digests']
): DigestFormFields[] => {
  return (digests ?? []).map(digest => ({
    stored: digest,
    heading: digest.heading ?? '',
    url: digest.url ?? '',
    item_selector: digest.item_selector ?? '',
    title_selector: digest.title_selector ?? '',
    date_attr: digest.date_attr ?? '',
    drop_past: digest.drop_past ?? true,
    max_items: digest.max_items ?? DIGEST_DEFAULT_MAX_ITEMS,
    page_url_template: digest.page_url_template ?? '',
    fetch_detail_pages: !!digest.fetch_detail_pages,
    detail_link_selector: digest.detail_link_selector ?? '',
    max_pages: digest.max_pages ?? DIGEST_DEFAULT_MAX_PAGES,
  }));
};

// A digest only means anything once it has all four backend-required
// selectors (see DigestRule.from_dict). DigestEntryFields' required rules
// block submitting a row missing one; this filter is only a backstop.
//
// update_knowledge replaces the stored digests list with this one, so each
// row's form fields are layered onto its stored digest - rebuilding from the
// form's fields alone would silently drop every DigestRule field the form has
// no input for (group_by, month_names, meta_selectors, note, ...).
const buildDigestsPayload = (
  digestEntries: DigestFormFields[] | undefined
): DigestConfig[] => {
  return (digestEntries ?? [])
    .map(entry => ({
      ...entry.stored,
      heading: entry.heading?.trim() || '',
      url: entry.url?.trim() || '',
      item_selector: entry.item_selector?.trim() || '',
      title_selector: entry.title_selector?.trim() || '',
      date_attr: entry.date_attr?.trim() || '',
      drop_past: entry.drop_past ?? true,
      max_items: entry.max_items ?? DIGEST_DEFAULT_MAX_ITEMS,
      page_url_template: entry.page_url_template?.trim() || undefined,
      fetch_detail_pages: !!entry.fetch_detail_pages,
      // The detail link selector only means anything once detail-page
      // fetching is on - dropped otherwise so a stale value left in the
      // form field never silently re-enables it server-side.
      detail_link_selector: entry.fetch_detail_pages
        ? entry.detail_link_selector?.trim() || undefined
        : undefined,
      max_pages: entry.max_pages ?? DIGEST_DEFAULT_MAX_PAGES,
    }))
    .filter(
      entry =>
        !!entry.url &&
        !!entry.item_selector &&
        !!entry.title_selector &&
        !!entry.date_attr
    );
};

// Shared shape for the webscrap fields registered by WebscrapConfigFields,
// wherever a knowledge base is authored.
interface WebscrapFormFields {
  webscrap_enabled?: boolean;
  webscrap_frequency?: WebscrapFrequency;
  webscrap_source_url?: string;
  webscrap_use_sitemap?: boolean;
  webscrap_max_depth?: number;
  webscrap_max_pages?: number;
  webscrap_pages_per_pdf?: number;
  webscrap_links?: { url: string; depth?: number; splitSelector?: string }[];
  webscrap_exclude_url_patterns?: { value: string }[];
  webscrap_scrape_images?: boolean;
  webscrap_digests?: DigestFormFields[];
}

// An absolute http(s) URL with a host. The backend's single-site check
// discards hosts it can't parse, so a bare "example.com" passes config
// validation and only fails at scrape time - this catches it at entry.
export const HTTP_URL_PATTERN = /^\s*https?:\/\/[^\s/?#]+\S*\s*$/i;

const isHttpUrl = (value?: string): boolean =>
  !!value && HTTP_URL_PATTERN.test(value);

const isFilled = (value?: string): boolean => !!value?.trim();

// Values-only mirror of the field rules in WebscrapConfigFields,
// WebscrapLinksField and DigestEntryFields, for callers that check a step
// without its fields mounted (the agent forms' Save-time step gate).
// The website URL is optional once an extra page or a digest is set,
// matching the backend's "source_page, links or digests" rule.
export const isWebscrapConfigValid = (
  values: Pick<
    WebscrapFormFields,
    'webscrap_enabled' | 'webscrap_source_url'
  > & {
    webscrap_links?: { url: string }[];
    webscrap_digests?: Pick<
      DigestFormFields,
      'url' | 'item_selector' | 'title_selector' | 'date_attr'
    >[];
  }
): boolean => {
  if (!values.webscrap_enabled) return true;
  const links = values.webscrap_links ?? [];
  const digests = values.webscrap_digests ?? [];
  const sourceUrl = values.webscrap_source_url;
  if (
    isFilled(sourceUrl)
      ? !isHttpUrl(sourceUrl)
      : !links.length && !digests.length
  ) {
    return false;
  }
  return (
    links.every(link => isHttpUrl(link.url)) &&
    digests.every(
      digest =>
        isHttpUrl(digest.url) &&
        isFilled(digest.item_selector) &&
        isFilled(digest.title_selector) &&
        isFilled(digest.date_attr)
    )
  );
};

// The exclude-URLs field asks users for a plain URL (or URL fragment), not
// a regex, but the backend's exclude_url_patterns field only accepts regex
// strings - so every entered URL is escaped into a literal-substring regex
// before it's sent, matching any scraped page whose URL contains it.
export const escapeUrlForRegex = (url: string): string => {
  return url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

// The inverse of escapeUrlForRegex: strips the backslash it put before each
// metacharacter, so the field shows the URL the user typed and the next save
// escapes it exactly once again instead of escaping the escaped regex.
const unescapeUrlFromRegex = (pattern: string): string => {
  return pattern.replace(/\\([.*+?^${}()|[\]\\])/g, '$1');
};

// The inverse of buildWebscrapPayload's exclude_url_patterns handling. Used
// to seed the form's defaultValues from an existing knowledge's webscrap
// config.
export const parseExcludeUrlPatterns = (
  patterns?: WebscrapConfig['exclude_url_patterns']
): { value: string }[] => {
  return (patterns ?? []).map(pattern => ({
    value: unescapeUrlFromRegex(pattern),
  }));
};

const buildWebscrapPayload = (data: WebscrapFormFields): WebscrapConfig => {
  const sourceUrl = data.webscrap_source_url?.trim() || undefined;
  // max_depth matters with the sitemap on too: the backend falls back to a
  // crawl at that depth when the site has no sitemap (discovery.py). So the
  // object form is sent whenever a depth is set; a bare string would drop it
  // and the backend would use the top-level max_depth instead.
  const useSitemap = data.webscrap_use_sitemap ?? true;
  const maxDepth = data.webscrap_max_depth;
  const excludePatterns = (data.webscrap_exclude_url_patterns ?? [])
    .map(entry => entry.value?.trim())
    .filter((value): value is string => !!value)
    .map(escapeUrlForRegex);
  const digests = buildDigestsPayload(data.webscrap_digests);
  const links: WebscrapConfig['links'] = (data.webscrap_links ?? [])
    .map(entry => ({
      url: entry.url?.trim(),
      depth: entry.depth,
      splitSelector: entry.splitSelector?.trim() || undefined,
    }))
    .filter(
      (
        entry
      ): entry is {
        url: string;
        depth: number | undefined;
        splitSelector: string | undefined;
      } => !!entry.url
    )
    .map(entry =>
      entry.depth === undefined && entry.splitSelector === undefined
        ? entry.url
        : {
            url: entry.url,
            ...(entry.depth !== undefined ? { depth: entry.depth } : {}),
            ...(entry.splitSelector !== undefined
              ? { split_selector: entry.splitSelector }
              : {}),
          }
    );

  // update_knowledge shallow-merges this onto the stored webscrap config and
  // JSON drops undefined keys, so a field the user emptied must be sent as
  // null/[] - left undefined, it never reaches the backend and the old value
  // stays. The backend's WebscrapConfig treats null and [] as "not set".
  return {
    enabled: !!data.webscrap_enabled,
    frequency: data.webscrap_frequency,
    source_page: !sourceUrl
      ? null
      : useSitemap && maxDepth === undefined
        ? sourceUrl
        : {
            url: sourceUrl,
            use_sitemap: useSitemap,
            max_depth: maxDepth ?? WEBSCRAP_DEFAULT_MAX_DEPTH,
          },
    links,
    max_pages: data.webscrap_max_pages ?? WEBSCRAP_DEFAULT_MAX_PAGES,
    pages_per_pdf:
      data.webscrap_pages_per_pdf ?? WEBSCRAP_DEFAULT_PAGES_PER_PDF,
    exclude_url_patterns: excludePatterns,
    scrape_images: !!data.webscrap_scrape_images,
    digests,
  };
};

// Persists the webscrap config onto an existing knowledge base. Never throws:
// a bad config must not abort the rest of the save, so this returns a warning
// (including the backend's reason, e.g. an invalid exclude pattern) for the
// caller to fold into its final toast. Showing it straight away doesn't work -
// the snackbar holds one message and the success toast that follows would
// replace it. Returns undefined when the config was saved.
const saveWebscrapConfig = async (
  knowledgeId: string,
  data: WebscrapFormFields,
  t: (key: TranslationKey) => string
): Promise<string | undefined> => {
  let reason: unknown;
  try {
    const result = await updateKnowledge(knowledgeId, {
      webscrap: buildWebscrapPayload(data),
    });
    if (result?.status === 'success') return undefined;
    reason = result?.message;
  } catch (error) {
    reason = error instanceof Error ? error.message : error;
  }
  return [
    t('The web scraping config could not be saved.'),
    typeof reason === 'string' ? reason : '',
  ]
    .filter(Boolean)
    .join(' ');
};

// Never throws for an individual file's failure - like deleteKnowledgeFiles,
// callers get back which specific files failed so one bad/slow upload can't
// hide the batch's otherwise-successful ones behind a single rejection.
const uploadKnowledgeFiles = async (
  knowledgeFilesData: KnowledgeFileData[],
  newKnowledge: Knowledge
): Promise<{ failed: string[] }> => {
  const failed: string[] = [];
  const queue = [...knowledgeFilesData];

  const worker = async () => {
    let next = queue.shift();
    while (next) {
      try {
        await createKnowledgeFile(newKnowledge?.id ?? '', next);
      } catch {
        failed.push(next.name);
      }
      next = queue.shift();
    }
  };

  const workerCount = Math.min(
    KNOWLEDGE_FILE_UPLOAD_CONCURRENCY,
    knowledgeFilesData.length
  );
  await Promise.all(Array.from({ length: workerCount }, worker));

  return { failed };
};

// Unlike uploadKnowledgeFiles (Promise.all, one rejection fails the whole
// batch), deletions use Promise.allSettled so a failure on one file doesn't
// block the rest from completing - callers inspect the settled results to
// report which specific file(s) failed.
const deleteKnowledgeFiles = async (
  fileNames: string[],
  knowledgeId: string
): Promise<PromiseSettledResult<ResponseObject>[]> => {
  return Promise.allSettled(
    fileNames.map(fileName => deleteKnowledgeFile(knowledgeId, fileName))
  );
};

// Only PDFs can be previewed: the backend refuses inline URLs for other types
const isPreviewableFile = (fileName: string): boolean =>
  fileName.toLowerCase().endsWith('.pdf');

export {
  buildWebscrapPayload,
  bytesToMegaBytesLabel,
  createNewKnowledge,
  deleteKnowledgeFiles,
  getOrCreateKnowledgeId,
  isPreviewableFile,
  isWebscrapInProgress,
  saveWebscrapConfig,
  truncateFilename,
  toSnakeCase,
  uploadKnowledgeFiles,
};
